import { v4 as uuidv4 } from "uuid";

export interface LocationNode {
  name: string;
  abbreviation: string;
  description: string;
  isDataPlate: boolean;
  rectInStructImage: { top: number | null; left: number | null; width: number | null; height: number | null } | null;
  children: LocationNode[];
  inspectionFor?: string[];
}

export interface AssetTypeExport {
  AssetTypes: AssetType[];
  AssetTypeRelations: AssetTypeRelation[];
  AssetTypeProperties: AssetTypeProperty[];
  AssetTypeStructureImages: AssetTypeStructureImage[];
  AssetTypeResources: unknown[];
}

export interface AssetTypeProperty {
  Property: {
    Name: string;
    Id: string;
    Type: number;
    IsSystem: boolean;
  };
  AssetTypeId: string;
  Value: string;
  IsMandatory: boolean;
  IsInherited: boolean;
  IsReadOnly: boolean;
}

export interface AssetType {
  Id: string;
  Name: string;
  ShortName: string;
  DisplayName: string;
  Category: AssetTypeCategory | null;
  Description: string;
  Creator: string;
  IsEnabled: boolean;
}

export interface AssetTypeCategory {
  Id: string;
  Name: string;
  IconName: string;
}

export interface AssetTypeStructureImage {
  FileName: string;
  ContentType: string;
  AssetTypeId: string;
  SourceFilePath: string;
}

export interface AssetTypeRelation {
  ParentId: string;
  ChildId: string;
  Occurence: number;
  ChildLabel: string;
  Location: AssetTypeLocation | null;
  Position: number;
}

export interface AssetTypeLocation {
  Points: LocationPoint[];
}

export interface LocationPoint {
  X: number;
  Y: number;
}

export interface AssetTypeExportResult {
  export: AssetTypeExport;
  structImageEntries: AssetTypeStructureImage[];
}

export class IdsMap extends Map<string, string> {
  toJSON(): Record<string, string> {
    return Object.fromEntries(this);
  }
}

export const CategoryDict: Record<string, { id: string; iconName: string }> = {
  dataplate: { id: "00000000-0000-0000-0000-000000000001", iconName: "" },
  externalView: { id: "00000000-0000-0000-0000-000000000002", iconName: "" },
  airframe: { id: "00000000-0000-0000-0000-000000000003", iconName: "" },
  booster: { id: "00000000-0000-0000-0000-000000000004", iconName: "" },
  inlet: { id: "00000000-0000-0000-0000-000000000005", iconName: "" },
  inletGuideVane: { id: "00000000-0000-0000-0000-000000000006", iconName: "" },
  fanBlade: { id: "00000000-0000-0000-0000-000000000007", iconName: "" },
  rotorBlade: { id: "00000000-0000-0000-0000-000000000008", iconName: "" },
  statorVane: { id: "00000000-0000-0000-0000-000000000009", iconName: "" },
  combustionChamber: { id: "00000000-0000-0000-0000-000000000010", iconName: "combustionChamber" },
  fuelNozzle: { id: "00000000-0000-0000-0000-000000000011", iconName: "" },
  liner: { id: "00000000-0000-0000-0000-000000000012", iconName: "" },
  deflector: { id: "00000000-0000-0000-0000-000000000013", iconName: "" },
  impeller: { id: "00000000-0000-0000-0000-000000000014", iconName: "" },
  compressor: { id: "00000000-0000-0000-0000-000000000015", iconName: "compressor" },
  highPressureCompressor: { id: "00000000-0000-0000-0000-000000000016", iconName: "compressor" },
  intermediatePressureCompressor: { id: "00000000-0000-0000-0000-000000000017", iconName: "compressor" },
  lowPressureCompressor: { id: "00000000-0000-0000-0000-000000000018", iconName: "compressor" },
  turbine: { id: "00000000-0000-0000-0000-000000000019", iconName: "turbine" },
  highPressureTurbine: { id: "00000000-0000-0000-0000-000000000020", iconName: "turbine" },
  intermediatePressureTurbine: { id: "00000000-0000-0000-0000-000000000021", iconName: "turbine" },
  lowPressureTurbine: { id: "00000000-0000-0000-0000-000000000022", iconName: "turbine" },
  shroud: { id: "00000000-0000-0000-0000-000000000023", iconName: "" },
  nozzle: { id: "00000000-0000-0000-0000-000000000024", iconName: "" },
  nozzleGuideVane: { id: "00000000-0000-0000-0000-000000000025", iconName: "" },
  manifold: { id: "00000000-0000-0000-0000-000000000026", iconName: "" },
  bellmouth: { id: "00000000-0000-0000-0000-000000000027", iconName: "" },
  outletGuideVane: { id: "00000000-0000-0000-0000-000000000028", iconName: "" },
  airfoil: { id: "00000000-0000-0000-0000-000000000029", iconName: "" },
  combustor: { id: "00000000-0000-0000-0000-000000000030", iconName: "" },
  burner: { id: "00000000-0000-0000-0000-000000000031", iconName: "" },
  heatShield: { id: "00000000-0000-0000-0000-000000000032", iconName: "" },
  igniter: { id: "00000000-0000-0000-0000-000000000033", iconName: "" },
  swirler: { id: "00000000-0000-0000-0000-000000000034", iconName: "" },
  domeAssembly: { id: "00000000-0000-0000-0000-000000000035", iconName: "" },
  valve: { id: "00000000-0000-0000-0000-000000000036", iconName: "" },
  frame: { id: "00000000-0000-0000-0000-000000000037", iconName: "" },
  gearbox: { id: "00000000-0000-0000-0000-000000000038", iconName: "" },
  exhaust: { id: "00000000-0000-0000-0000-000000000039", iconName: "exhaust" },
  auxiliary: { id: "00000000-0000-0000-0000-000000000040", iconName: "" },
  diffuser: { id: "00000000-0000-0000-0000-000000000041", iconName: "" },
  condenser: { id: "00000000-0000-0000-0000-000000000042", iconName: "" },
  generator: { id: "00000000-0000-0000-0000-000000000043", iconName: "" },
  strut: { id: "00000000-0000-0000-0000-000000000044", iconName: "" },
  other: { id: "00000000-0000-0000-0000-000000000045", iconName: "" },
  oil: { id: "00000000-0000-0000-0000-000000000046", iconName: "" },
  displacement: { id: "00000000-0000-0000-0000-000000000047", iconName: "" },
  magnet: { id: "00000000-0000-0000-0000-000000000048", iconName: "" },
  filter: { id: "00000000-0000-0000-0000-000000000049", iconName: "" },
  race: { id: "00000000-0000-0000-0000-000000000050", iconName: "" },
  cage: { id: "00000000-0000-0000-0000-000000000051", iconName: "" },
  shoulder: { id: "00000000-0000-0000-0000-000000000052", iconName: "" },
  roller: { id: "00000000-0000-0000-0000-000000000053", iconName: "" },
  assembly: { id: "00000000-0000-0000-0000-000000000054", iconName: "" },
  highSpeed: { id: "00000000-0000-0000-0000-000000000055", iconName: "" },
  intermediateSpeed: { id: "00000000-0000-0000-0000-000000000056", iconName: "" },
  lowSpeed: { id: "00000000-0000-0000-0000-000000000057", iconName: "" },
  rotor: { id: "00000000-0000-0000-0000-000000000058", iconName: "" },
  upwind: { id: "00000000-0000-0000-0000-000000000059", iconName: "" },
  downwind: { id: "00000000-0000-0000-0000-000000000060", iconName: "" },
  dedendum: { id: "00000000-0000-0000-0000-000000000061", iconName: "" },
  addendum: { id: "00000000-0000-0000-0000-000000000062", iconName: "" },
  pinion: { id: "00000000-0000-0000-0000-000000000063", iconName: "" },
  shaft: { id: "00000000-0000-0000-0000-000000000064", iconName: "" },
  gear: { id: "00000000-0000-0000-0000-000000000065", iconName: "" },
  planetaryGear: { id: "00000000-0000-0000-0000-000000000066", iconName: "" },
  ringGear: { id: "00000000-0000-0000-0000-000000000067", iconName: "" },
  sunGear: { id: "00000000-0000-0000-0000-000000000068", iconName: "" },
  bearing: { id: "00000000-0000-0000-0000-000000000069", iconName: "" },
  carrierBearing: { id: "00000000-0000-0000-0000-000000000070", iconName: "" },
  rollerBearing: { id: "00000000-0000-0000-0000-000000000071", iconName: "" },
  planetaryBearing: { id: "00000000-0000-0000-0000-000000000072", iconName: "" },
  location: { id: "00000000-0000-0000-0000-000000000073", iconName: "location" },
  aircraft: { id: "00000000-0000-0000-0000-000000000074", iconName: "aircraft" },
  aircraftEngine: { id: "00000000-0000-0000-0000-000000000075", iconName: "aircraft" },
  windTurbine: { id: "00000000-0000-0000-0000-000000000076", iconName: "windTurbine" },
  windGearbox: { id: "00000000-0000-0000-0000-000000000077", iconName: "windTurbine" },
  helicopter: { id: "00000000-0000-0000-0000-000000000078", iconName: "helicopter" },
  engine: { id: "00000000-0000-0000-0000-000000000079", iconName: "engine" },
  fuelLine: { id: "00000000-0000-0000-0000-000000000080", iconName: "fuelLine" },
  gasTurbine: { id: "00000000-0000-0000-0000-000000000081", iconName: "fuelLine" },
  blade: { id: "00000000-0000-0000-0000-000000000082", iconName: "blade" },
  landingGear: { id: "00000000-0000-0000-0000-000000000083", iconName: "landingGear" },
  fuelTank: { id: "00000000-0000-0000-0000-000000000084", iconName: "fuelTank" }
};

// Based on legacy to Asset module mapping from documentation: https://evident-products.atlassian.net/wiki/x/XYDaO
export const LocationNameToCategory: Record<string, string> = {
  // --- Aviation Engine (AE) ---
  "Aviation Engine": "aircraftEngine",
  INLET: "inlet",
  "Low Pressure Compressor": "lowPressureCompressor",
  "High Pressure Compressor": "highPressureCompressor",
  "Combustion Chamber": "combustionChamber",
  "High Pressure Turbine": "highPressureTurbine",
  "Nozzle Guide Vane": "nozzleGuideVane",
  Shroud: "shroud",
  "Low Pressure Turbine": "lowPressureTurbine",
  // AE blade children (leading/trailing edge stages)
  "Stage 01 Leading Edge": "rotorBlade",
  "Stage 01 Trailing Edge": "rotorBlade",
  "Stage 02 Leading Edge": "rotorBlade",
  "Stage 02 Trailing Edge": "rotorBlade",
  "Stage 03 Leading Edge": "rotorBlade",
  "Stage 03 Trailing Edge": "rotorBlade",
  "Stage 04 Leading Edge": "rotorBlade",
  "Stage 04 Trailing Edge": "rotorBlade",
  "Stage 05 Leading Edge": "rotorBlade",
  "Stage 05 Trailing Edge": "rotorBlade",
  "Stage 06 Leading Edge": "rotorBlade",
  "Stage 06 Trailing Edge": "rotorBlade",
  "Stage 07 Leading Edge": "rotorBlade",
  "Stage 07 Trailing Edge": "rotorBlade",
  "Stage 08 Leading Edge": "rotorBlade",
  "Stage 08 Trailing Edge": "rotorBlade",
  "Stage 09 Leading Edge": "rotorBlade",
  "Stage 09 Trailing Edge": "rotorBlade",
  "Stage 10 Leading Edge": "rotorBlade",
  "Stage 10 Trailing Edge": "rotorBlade",
  // --- Industrial Gas Turbine (GAS) ---
  "Industrial Gas Turbine": "gasTurbine",
  "Stage 00": "rotorBlade",
  "Stage 01": "rotorBlade",
  "Stage 02": "rotorBlade",
  "Stage 03": "rotorBlade",
  "Stage 04": "rotorBlade",
  "Stage 05": "rotorBlade",
  "Stage 06": "rotorBlade",
  "Stage 07": "rotorBlade",
  "Stage 08": "rotorBlade",
  "Stage 09": "rotorBlade",
  "Stage 10": "rotorBlade",
  "Stage 11": "rotorBlade",
  "Stage 12": "rotorBlade",
  "Stage 14": "rotorBlade",
  // --- Wind Turbine Gearbox (WIND) ---
  "Wind Turbine Gearbox": "windGearbox",
  "High-Speed Pinion Shaft": "shaft",
  "Low-Speed Gear": "gear",
  "Intermediate Pinion Shaft": "shaft",
  "Intermediate Gear": "gear",
  "Sun Pinion": "pinion",
  "Planetary Gears": "planetaryGear",
  "Planetary Gear 1": "planetaryGear",
  "Planetary Gear 2": "planetaryGear",
  "Planetary Gear 3": "planetaryGear",
  "Ring Gear": "ringGear",
  "High-Speed Rotor Side Cylindrical Roller Bearing": "rollerBearing",
  "High-Speed Gen Side Tapered Roller Bearing": "rollerBearing",
  "Intermediate Gen Side Tapered Roller Bearing": "rollerBearing",
  "Intermediate Rotor Side Cylindrical Roller Bearing": "rollerBearing",
  "Low-Speed Gen Side Tapered Roller Bearing": "rollerBearing",
  "Low-Speed Rotor Side Cylindrical Roller Bearing": "rollerBearing",
  "Planetary Bearings": "planetaryBearing",
  "Planet Bearing 1 R Side Half Cyl Roller Bearing": "rollerBearing",
  "Planet Bearing 1 G Side Half Cyl Roller Bearing": "rollerBearing",
  "Planet Bearing 2 R Side Half Cyl Roller Bearing": "rollerBearing",
  "Planet Bearing 2 G Side Half Cyl Roller Bearing": "rollerBearing",
  "Planet Bearing 3 R Side Half Cyl Roller Bearing": "rollerBearing",
  "Planet Bearing 3 G Side Half Cyl Roller Bearing": "rollerBearing",
  "Gen Side Carrier Bearing Cylindrical Roller": "carrierBearing",
  "Rotor Side Carrier Bearing Cylindrical Roller": "carrierBearing"
};

export const SerialNumberProperty = {
  Name: "SerialNumber",
  Id: uuidv4(),
  Type: 0,
  IsSystem: true
};

export const CreatorName = "IPLEX User";
